import { Col, Container, Row } from 'react-bootstrap';
import { Dispatch, SetStateAction } from 'react';
import { TODAY, TODAY_EOD } from '@/constants';

import Feature from '@/common/Feature';
import LostAndFoundHistoryFilters from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryFilters';
import LostAndFoundHistoryTable from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryTable';
import { LostAndFoundIssueWithDocuments } from './components/LostAndFoundHistoryForm/types';
import { ServiceIssueFiltersType } from '@/hooks/useServiceIssueFilters';
import { SortDirectionEnum } from '@/models/gen/graphql';
import { refetchLostAndFoundIssueWithDocuments } from '@/features/LostAndFoundHistory/api/services/searchLostAndFoundIssue';
import { useProvider } from '@/common/Provider';

const LostAndFoundHistory = (): JSX.Element => {
  return (
    <Feature name="LostAndFoundHistory" context={initLostAndFoundHistoryState} methods={lostandfoundHistoryMethodConstructor}>
      <LostAndFoundHistoryFilters />
      <Container>
        <Row>
          <Col>
            <LostAndFoundHistoryTable />
          </Col>
        </Row>
      </Container>
    </Feature>
  );
};

export type LostAndFoundHistoryFiltersType = {
  startDatetime: string;
  endDatetime: string;
  airportCode: string[];
  tripServicerIataAirlineCodes: string[];
  returnStatuses: string[];
};
export type LostAndFoundHistoryRow = LostAndFoundIssueWithDocuments;
export type LostAndFoundHistoryContext = {
  filters: ServiceIssueFiltersType;
  reports: Map<string, LostAndFoundHistoryRow>;
  totalReports: number;
  selected: Map<string, LostAndFoundHistoryRow>;
  loading: boolean;
  sortBy: string;
  sortDirection: SortDirectionEnum;
  search: string;
};
export type LostAndFoundHistoryMethods = {
  resetState: (overrides?: Partial<LostAndFoundHistoryContext>) => void;
  submitFilters: (values?: Partial<LostAndFoundHistoryFiltersType>) => Promise<void>;
};
export const initLostAndFoundHistoryState: LostAndFoundHistoryContext = {
  filters: {
    startDatetime: TODAY,
    endDatetime: TODAY_EOD,
    airportCode: [],
    tripServicerIataAirlineCodes: [],
    returnStatuses: [],
  },
  search: '',
  reports: new Map(),
  totalReports: 0,
  selected: new Map(),
  sortBy: '',
  sortDirection: SortDirectionEnum.Asc,
  loading: false,
};
export const lostandfoundHistoryMethodConstructor = (
  state: LostAndFoundHistoryContext,
  setState: Dispatch<SetStateAction<LostAndFoundHistoryContext>>
): LostAndFoundHistoryMethods => ({
  resetState: (overrides?: Partial<LostAndFoundHistoryContext>): void =>
    setState({ ...initLostAndFoundHistoryState, ...(overrides || {}) }),
  submitFilters: async (values: ServiceIssueFiltersType = state.filters): Promise<void> => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await refetchLostAndFoundIssueWithDocuments([values]);
      setState((current) => {
        const reports = new Map<string, LostAndFoundHistoryRow>(res.rows.map((issue) => [issue.id, issue]));
        const totalReports = res.totalCount;
        return { ...current, reports, totalReports };
      });
    } catch (err) {
      console.error('Error submitting filters:', err);
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  },
});
export function useLostAndFoundHistory<T = LostAndFoundHistoryContext>(
  selector?: (state: LostAndFoundHistoryContext) => T
): [T, Dispatch<SetStateAction<LostAndFoundHistoryContext>>, LostAndFoundHistoryMethods] {
  return useProvider<LostAndFoundHistoryContext, T, LostAndFoundHistoryMethods>(selector);
}

export default LostAndFoundHistory;
